<template>
  <div class="leftCardContainer">
    <div class="rightSideTitleActions">
      <span class="leftTitle">{{ $t("Directory") }}</span>
      <div class="actionsContainer">
        <div class="searchContainer">
          <v-icon> search </v-icon>
          <InputField
            inputType="text"
            :value="search"
            :placeholder="$t('Search')"
            :inputStyle="{
              borderRadius: '0px',
              width: '130px',
              borderTop: 'none',
              borderRight: 'none',
              borderLeft: 'none',
              borderBottom: '1px solid rgba(118, 118, 118, 0.50)',
            }"
            @input="handleSearch($event)"
          />
        </div>
        <CustomButton
          icon="delete"
          :width="'20px'"
          :height="'20px'"
          :iconSize="12"
          :disabled="disableDeleteButton"
          :buttonStyling="{
            padding: '8px',
            marginRight: '5px',
            paddingTop: '10px',
            borderRadius: '4px',
          }"
          @buttonClick="openDeleteDialog"
        />
        <CustomButton
          icon="download"
          :width="'20px'"
          :height="'20px'"
          :iconSize="12"
          :disabled="false"
          :buttonStyling="{
            padding: '8px',
            paddingTop: '10px',
            marginRight: '5px',
            borderRadius: '4px',
          }"
          @buttonClick="handleDownload"
        />
        <CustomButton
          icon="add"
          :width="'20px'"
          :height="'20px'"
          :iconSize="12"
          :buttonStyling="{
            padding: '8px',
            marginRight: '5px',
            paddingTop: '10px',
            borderRadius: '4px',
          }"
          :disabled="userIsCreatingOrEditingData"
          @buttonClick="handleAdd"
        />
      </div>
    </div>
    <div class="tableDivContainer">
      <Table
        :checkbox="false"
        :loading="loading"
        :headers="headers"
        :items="workspaces"
        :sortable="true"
        :emitRowClickBusEvent="!userIsCreatingOrEditingData"
        @onScrollBottom="onScrollBottom"
        @onScrollTop="onScrollTop"
        @sortClick="onSortClick"
      />
    </div>
    <dialog-choice
      :isOpen="deleteDialogIsOpen"
      :title="$t('DeleteWorkspace')"
      :content="$t('DeleteWorkspaceConfirmation')"
      :mainActionLabel="$t('Confirm')"
      @mainAction="handleDelete"
      :secondaryActionLabel="$t('Cancel')"
      @secondaryAction="deleteDialogIsOpen = false"
      icon="mdi-delete"
      iconColor="var(--app-primary-base)"
    />
    <loading-spinner
      :active.sync="loading"
      :is-full-page="false"
      color="var(--app-primary-base)"
      style="position: absolute"
    />
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { bus } from "@/main.js";
import { managementDatatableMixin } from "@/mixins/management/managementDatatableMixin";
import ManagementPage from "@/newComponents/_Organisms/ManagementPage.vue";
import Table from "@/newComponents/_Molecules/Table.vue";
import InputField from "@/newComponents/_Atoms/InputField.vue";
import { checkPermissionsMixin } from "@/mixins/checkPermissionsMixin";
import CustomButton from "@/newComponents/_Atoms/CustomButton.vue";
import LoadingSpinner from "vue-loading-overlay";
import DialogChoice from "@/newComponents/_Molecules/DialogChoice.vue";
import { listsMixin } from "@/mixins/listsMixin";
export default {
  name: "TableView",

  mixins: [managementDatatableMixin, checkPermissionsMixin, listsMixin],

  components: {
    Table,
    ManagementPage,
    InputField,
    CustomButton,
    LoadingSpinner,
    DialogChoice,
  },

  props: {
    selectedItem: {
      type: Object,
    },
  },

  data() {
    return {
      value: null,
      deleteDialogIsOpen: false,
      search: null,
      activeTab: 0,
      loading: false,
      scrollTop: 0,
      sortOrder: null,
      scrollHeight: 0,
      pageNumber: 1,
      pagination: 25,
      currentPageTop: 1,
      currentPageBot: 1,
      lastPage: 2,
      workspaces: [],
      projectPhases: [],
      userIsCreatingOrEditingData: false,
    };
  },

  mounted() {
    this.getInitialWorkspaces();
    this.getProjectPhases();
    bus.$on("reload-workspaces", () => this.getInitialWorkspaces());
    bus.$on("toggle-create-edit-flag", () => this.userIsCreatingOrEditingData = !this.userIsCreatingOrEditingData)
  },

  destroyed() {
    bus.$off("reload-workspaces");
    bus.$off("toggle-create-edit-flag");
  },

  computed: {
    headers() {
      return [
        { text: this.$i18n.tc("Label", 1), varName: "wp_label" },
        { text: this.$i18n.t("PlanName"), varName: "wp_plan_name" },
        { text: this.$i18n.t("Phase"), varName: "wp_status" },
        { text: this.$i18n.t("LastUpdate"), varName: "wp_updated_at" },
        { text: this.$i18n.t("CreationDate"), varName: "wp_created_at" },
        { text: this.$i18n.t("Status"), varName: "wp_status_active" },
        { text: this.$i18n.t("Access"), varName: "wp_access_active" },
      ];
    },
    disableDeleteButton(){
      return !this.selectedItem || Object.keys(this.selectedItem).length === 0;
    }
  },

  methods: {
    ...mapActions([
      "fetchWorkspaces",
      "DELETE_WORKSPACE",
      "downloadAllWorkspaces",
    ]),

    getProjectPhases() {
      this.GET_LIST({ listName: "list_statut", code: [], includeDeleted: true })
        .then((projectPhases) => {
          this.projectPhases = projectPhases;
        })
        .catch((error) => {
          console.error(error);
        });
    },

    getProjectPhaseName(id) {
      if (id && this.projectPhases.length > 0) {
        let phase = this.projectPhases.find((item) => item.code === id);
        return this.getListElementLabel(phase);
      } else return id;
    },

    async getInitialWorkspaces() {
      this.loading = true;
      this.currentPageBot = 1;
      this.currentPageTop = 1;
      let queryParams = {
        "page[number]": 1,
        paginate: 25,
      };

      if (this.search !== null) {
        queryParams["filter[search]"] = this.search;
      }

      if (this.sortOrder !== null) {
        queryParams["sort"] = this.sortOrder;
      }

      this.getWorkspaces(queryParams);
    },

    getWorkspaces(queryParams, scrollDirection = null) {
      this.getAll(queryParams, scrollDirection);
    },

    async getAll(queryParams = null, scrollDirection = null) {
      this.loading = true;
      try {
        let workspaces = await this.fetchWorkspaces(queryParams);
        this.processWorkspacesData(workspaces, scrollDirection);
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
        this.$nextTick(() =>
          this.updateLocalPagination(this.paginationFromParent)
        );
      }
    },

    processWorkspacesData(workspacesData, scrollDirection = null) {
      let allWorkspaces = null;
      let currentScroll = null;
      let rowElement = null;
      switch (scrollDirection) {
        case "top":
          this.lastPage = workspacesData.meta.last_page;
          allWorkspaces = [...workspacesData.data, ...this.workspaces];
          this.workspaces = Array.from(
            new Set(allWorkspaces.map((a) => a.sm_id))
          ).map((id) => {
            return allWorkspaces.find((a) => a.sm_id === id);
          });
          if (this.workspaces.length > 75) {
            this.workspaces.splice(-(this.workspaces.length - 75));
            this.currentPageBot--;
          }

          currentScroll = document.getElementById("scroll-bar").scrollTop;
          this.loading = false;

          rowElement = document.getElementById("rowId");
          if (rowElement) {
            const rowHeight = rowElement.clientHeight;
            for (let i = 0; i < 25; i++) {
              const offset = rowHeight * i;
              document.getElementById("scroll-bar").scrollTo({
                top: currentScroll + offset,
                behavior: "smooth",
              });
            }
          }
          break;
        case "bottom":
          this.lastPage = workspacesData.meta.last_page;
          allWorkspaces = [...this.workspaces, ...workspacesData.data];
          this.workspaces = Array.from(
            new Set(allWorkspaces.map((a) => a.wp_id))
          ).map((id) => {
            return allWorkspaces.find((a) => a.wp_id === id);
          });
          if (this.workspaces.length > 75) {
            this.workspaces = this.workspaces.slice(
              this.workspaces.length - 75
            );
          }

          currentScroll = document.getElementById("scroll-bar").scrollTop;
          this.loading = false;

          rowElement = document.getElementById("rowId");
          if (rowElement) {
            const rowHeight = rowElement.clientHeight;
            for (let i = 0; i < 2; i++) {
              const offset = rowHeight * i;
              document.getElementById("scroll-bar").scrollTo({
                top: currentScroll - offset,
                behavior: "smooth",
              });
            }
          }
          break;
        default:
          this.workspaces = workspacesData.data;
          this.lastPage = workspacesData.meta.last_page;
          break;
      }
      let workspacesCopy = [];
      this.workspaces.forEach((workspace) => {
        let workspaceCopy = {...workspace};
        workspaceCopy.wp_status = this.getProjectPhaseName(workspace.wp_status);
        workspaceCopy.wp_status_active = workspace.wp_status_active
          ? "Open"
          : "Closed";
        workspaceCopy.wp_access_active = workspace.wp_access_active
          ? "Visible"
          : "Not visible";
          workspaceCopy.wp_created_at = this.dateFormatter(workspace.wp_created_at);
          workspaceCopy.wp_updated_at = this.dateFormatter(workspace.wp_updated_at);
          workspacesCopy.push(workspaceCopy);
      });
      this.workspaces = workspacesCopy;
    },

    getWorkspacesBottom() {
      this.loading = true;
      this.pagination = 25;
      if (this.workspaces.length >= 75) {
        this.currentPageTop++;
      }
      this.currentPageBot++;

      let queryParams = {
        "page[number]": this.currentPageBot,
        paginate: this.pagination,
      };

      if (this.search !== null) {
        queryParams["filter[search]"] = this.search;
      }

      if (this.sortOrder !== null) {
        queryParams["sort"] = this.sortOrder;
      }

      this.getWorkspaces(queryParams, "bottom");
    },

    dateFormatter(date) {
      const dateArray = date.split(" ");
      return dateArray[0] + " / " + dateArray[1];
    },

    async getWorkspacesTop() {
      this.loading = true;
      this.pagination = 25;
      this.currentPageTop--;

      let queryParams = {
        "page[number]": this.currentPageTop,
        paginate: this.pagination,
      };

      if (this.search !== null) {
        queryParams["filter[search]"] = this.search;
      }

      if (this.sortOrder !== null) {
        queryParams["sort"] = this.sortOrder;
      }

      this.getWorkspaces(queryParams, "top");
    },

    handleSearch: _.debounce(function (event) {
      if (event === "") {
        this.search = null;
      } else {
        this.search = event;
      }
      this.workspaces = [];
      this.getInitialWorkspaces();
    }, 1000),

    onScrollBottom() {
      if (this.currentPageBot !== this.lastPage) {
        this.pageNumber++;
        this.getWorkspacesBottom();
      }
    },

    onScrollTop() {
      if (this.currentPageTop !== 1) {
        this.pageNumber--;
        this.getWorkspacesTop();
      }
    },

    onSortClick(sortOrder, index) {
      this.workspaces = [];
      if (sortOrder === "asc") {
        this.sortOrder = this.headers[index].varName;
      } else if (sortOrder === "desc") {
        this.sortOrder = `-${this.headers[index].varName}`;
      } else {
        this.sortOrder = sortOrder;
      }
      this.getInitialWorkspaces();
    },

    handleAdd() {
      this.$emit("open-create-workspace-form");

      setTimeout(() => {
        bus.$emit("reset-polygon");
      }, 100);
    },

    async handleDownload() {
      try {
        this.loading = true;
        let data = await this.downloadAllWorkspaces();
        window.open(data.file_url_public);
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },

    openDeleteDialog() {
      if (this.selectedItem) {
        this.deleteDialogIsOpen = true;
      }
    },

    async handleDelete() {
      try {
        await this.DELETE_WORKSPACE(this.selectedItem.wp_id);
        this.getInitialWorkspaces();
        bus.$emit("clear-SelectedItems");
        bus.$emit("clear-selected-row");
        this.$emit("clear-selected-item");
        this.loading = true;
        this.$toasted.global.toast_success("Workspace deleted successfully");
      } catch (error) {
        this.$toasted.global.toast_error(error.response.data.data);
      } finally {
        this.deleteDialogIsOpen = false;
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.searchContainer {
  display: flex;
  align-items: center;
}

.tableDivContainer {
  height: 87%;
  margin-top: 5px;
}

.leftCardContainer {
  width: 100%;
  height: 100%;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0px 2px 2px 0px rgba(0, 0, 0, 0.25);
  padding: 20px 28px;
}

.actionsContainer {
  display: flex;
  align-items: center;
  gap: 3px;
}

.leftTitle {
  color: #767676;
  font-family: Lato;
  font-size: 10px;
  font-style: normal;
  font-weight: 800;
  line-height: 20px;
  text-transform: uppercase;
}

.rightSideTitleActions {
  height: 5%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.leftCardContainer {
  width: 100%;
  height: 100%;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0px 2px 2px 0px rgba(0, 0, 0, 0.25);
  padding: 20px 28px;
}

.actionsContainer {
  display: flex;
  align-items: center;
  gap: 3px;
}

.leftTitle {
  color: #767676;
  font-family: Lato;
  font-size: 10px;
  font-style: normal;
  font-weight: 800;
  line-height: 20px;
  /* 200% */
  text-transform: uppercase;
}

.rightSideTitleActions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}
</style>



// WEBPACK FOOTER //
// src/newComponents/Directory/TableView.vue